import { URLParams } from 'modules/url-params/state/app-url-params-types'
import { FormAddressValue, OrderAddressSlice } from './address-types'
import { StringSchema, ObjectSchema, AnyObject, object, string } from 'yup'

import { i18n } from 'next-i18next'
// @ts-expect-error i18n-zipcodes is a pure JS library and does not have types
import i18nZipcode from 'i18n-zipcodes'

export const DEFAULT_BILLING_DETAILS_ADDRESS_COUNTRY = 'US'

export const makeOrderAddressInitialState = (urlParams: URLParams): OrderAddressSlice => ({
  value: {
    country: urlParams.country || DEFAULT_BILLING_DETAILS_ADDRESS_COUNTRY,
    state: urlParams.state || '',
    city: urlParams.state || '',
    zipCode: urlParams.zip || '',
    line1: urlParams.street || '',
    line2: undefined,
  },
})

export const getLocalizedLabels = (country: string) => {
  const t = i18n?.t.bind(i18n) || ((key: string, fallback: string) => fallback)

  switch (country) {
    case 'US':
      return {
        state: t('AddressFields.US.state', 'State'),
        postalCode: t('AddressFields.US.zipCode', 'Zip Code'),
      }

    case 'CA':
      return {
        state: t('AddressFields.CA.state', 'Province'),
        postalCode: t('AddressFields.CA.zipCode', 'Postal Code'),
      }

    default:
      return {
        state: t('AddressFields.default.state', 'Region'),
        postalCode: t('AddressFields.default.zipCode', 'Postal Code'),
      }
  }
}

export const makeOrderAddressValidationSchema = (): ObjectSchema<FormAddressValue> => {
  const t = i18n?.t.bind(i18n) || ((key: string, fallback: string) => fallback)

  const schema = object({
    country: string().required(
      t('AddressFields.validations.country', 'Please provide your country'),
    ),
    state: string().required(t('AddressFields.validations.state', 'Please provide your state')),
    city: string().required(t('AddressFields.validations.city', 'Please provide your city')),
    zipCode: string().when('country', ([country], schema) => {
      const requiredLabel = getLocalizedLabels(country).postalCode

      schema = schema.required(
        `${requiredLabel} ${t('AddressFields.validations.isRequiredText', 'is required')}`,
      )

      schema = schema.test(
        'zipCode',
        `${requiredLabel} ${t('AddressFields.validations.isInvalidText', 'is invalid')}`,
        (value) => {
          if (!value) return true

          // If the country is not supported by i18n-zipcodes, it will throw an exception.
          // We will not validate the zip code and assume it is valid.
          try {
            return i18nZipcode(country, value)
          } catch (e) {
            return true
          }
        },
      )

      return schema
    }) as StringSchema<string, AnyObject, undefined, ''>,
    addressLine1: string().required(
      t('AddressFields.validations.address', 'Please provide your address'),
    ),
    addressLine2: string(),
  })

  return schema
}
